/**
 * [G-A1] и [G-A2] — ловушки, стоившие отдельных разборов на прошлых проектах.
 *
 * G-A1: скрипт telegram-web-app.js создаёт window.Telegram.WebApp В ЛЮБОМ БРАУЗЕРЕ.
 *       Проверка «объект существует» ложно-положительна. Единственный признак —
 *       НЕПУСТОЙ initData. На прошлом проекте эта ошибка давала 12 секунд
 *       «вечной загрузки» обычным посетителям сайта.
 *
 * G-A2: вне клиента метод существует, но выбрасывает WebAppMethodUnsupported,
 *       и обёртка вида `new Promise(res => tg.showConfirm(msg, res))` ВИСНЕТ
 *       НАВСЕГДА: кнопка нажимается, и молча ничего не происходит — ни в консоли,
 *       ни в логах сервера. Поэтому все вызовы идут через один защищённый помощник.
 */

export interface TelegramWebApp {
  initData: string
  initDataUnsafe?: { user?: { id: number; first_name?: string }; start_param?: string }
  colorScheme?: 'light' | 'dark'
  themeParams?: Record<string, string>
  version?: string
  /** tdesktop (Windows, Linux), macos, weba, webk, ios, android… */
  platform?: string
  isVersionAtLeast?: (v: string) => boolean
  /** Bot API 8.0: во весь экран. */
  isFullscreen?: boolean
  requestFullscreen?: () => void
  exitFullscreen?: () => void
  ready?: () => void
  expand?: () => void
  onEvent?: (e: string, cb: () => void) => void
  offEvent?: (e: string, cb: () => void) => void
  showConfirm?: (msg: string, cb: (ok: boolean) => void) => void
  showAlert?: (msg: string, cb?: () => void) => void
  BackButton?: { show?: () => void; hide?: () => void; onClick?: (cb: () => void) => void; offClick?: (cb: () => void) => void }
  MainButton?: { setText?: (t: string) => void; show?: () => void; hide?: () => void; onClick?: (cb: () => void) => void }
  HapticFeedback?: { impactOccurred?: (s: string) => void; notificationOccurred?: (s: string) => void }
  setHeaderColor?: (c: string) => void
  setBackgroundColor?: (c: string) => void
  setBottomBarColor?: (c: string) => void
  close?: () => void
  openLink?: (url: string) => void
  openTelegramLink?: (url: string) => void
  downloadFile?: (p: { url: string; file_name: string }, cb?: (ok: boolean) => void) => void
  CloudStorage?: unknown
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp }
  }
}

export function getWebApp(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null
}

/**
 * [G-A1] Признак «мы внутри Telegram» — ТОЛЬКО непустой initData
 * либо наличие tgWebAppData во фрагменте URL.
 * ⛔ Проверка «window.Telegram.WebApp существует» — ложно-положительна.
 */
export function isInsideTelegram(): boolean {
  const tg = getWebApp()
  if (tg && typeof tg.initData === 'string' && tg.initData.length > 0) return true
  return window.location.hash.includes('tgWebAppData')
}

export function getInitData(): string {
  const fromSdk = getWebApp()?.initData ?? ''
  if (fromSdk !== '') return fromSdk

  // ⚠️ РАСХОЖДЕНИЕ, НАЙДЕННОЕ 10.09.2026. isInsideTelegram() признаёт
  // фрагмент URL `#tgWebAppData=…` — так Telegram и передаёт строку, а SDK
  // лишь разбирает её. Но getInitData() читал ТОЛЬКО SDK, и при недоступном
  // telegram.org (блокировщик, сеть, задержка загрузки) приложение считало
  // себя внутри Telegram и отправляло ПУСТОЙ заголовок: сервер отвечал 401,
  // а человек видел «откройте приложение из Telegram», уже находясь в нём.
  //
  // Значение в фрагменте закодировано процентами — раскодируем один раз.
  const hash = window.location.hash.replace(/^#/, '')
  const raw = new URLSearchParams(hash).get('tgWebAppData')
  return raw ?? ''
}

/**
 * [G-A2] Единственная точка вызова методов Telegram.
 * Возвращает результат фоллбэка, если метода нет или он бросил исключение.
 * ⛔ Прямые вызовы tg.<метод>() в коде приложения запрещены.
 */
export function callTelegram<T>(
  fn: (tg: TelegramWebApp) => T,
  fallback: () => T,
): T {
  const tg = getWebApp()
  if (!tg || !isInsideTelegram()) return fallback()
  try {
    return fn(tg)
  } catch {
    // WebAppMethodUnsupported и прочее — молча уходим в фоллбэк,
    // но НЕ виснем: это и была исходная авария.
    return fallback()
  }
}

/**
 * Колбэк-методы Telegram оборачиваются в промис только здесь и только
 * с таймаутом: без него промис висит вечно, если клиент не ответит.
 */
export function callTelegramAsync<T>(
  fn: (tg: TelegramWebApp, resolve: (v: T) => void) => void,
  fallback: () => T,
  timeout_ms = 3000,
): Promise<T> {
  const tg = getWebApp()
  if (!tg || !isInsideTelegram()) return Promise.resolve(fallback())
  return new Promise<T>((resolve) => {
    let done = false
    const finish = (v: T): void => { if (!done) { done = true; resolve(v) } }
    // Таймер — обязателен. Он и есть защита от «виснет навсегда».
    const t = setTimeout(() => finish(fallback()), timeout_ms)
    try {
      fn(tg, (v) => { clearTimeout(t); finish(v) })
    } catch {
      clearTimeout(t)
      finish(fallback())
    }
  })
}

/** Подтверждение: внутри Telegram — родное, снаружи — window.confirm. */
export function confirmAction(message: string): Promise<boolean> {
  return callTelegramAsync<boolean>(
    (tg, resolve) => {
      if (!tg.showConfirm) throw new Error('showConfirm недоступен')
      tg.showConfirm(message, resolve)
    },
    () => window.confirm(message),
  )
}

export function haptic(style: 'light' | 'medium' | 'heavy' = 'light'): void {
  callTelegram(
    (tg) => { tg.HapticFeedback?.impactOccurred?.(style) },
    () => { /* вне Telegram вибрации нет — это не ошибка */ },
  )
}

export function signalReady(): void {
  callTelegram((tg) => { tg.ready?.(); tg.expand?.() }, () => { /* вне Telegram нечего сообщать */ })
}

/**
 * Вернуться в чат с ботом: мини-апп закрывается, человек оказывается в чате, где подключают ключ.
 * Имя бота клиенту для этого не нужно. Вне Telegram закрывать нечего — возвращаем false.
 */
export function backToChat(): boolean {
  return callTelegram((tg) => { if (!tg.close) return false; tg.close(); return true }, () => false)
}

/**
 * [F-7.3.9] Системная кнопка «назад» Telegram — единственная «назад» в приложении.
 * Видна, когда есть куда вернуться; на корне вкладки скрыта, и тогда «назад» Telegram (и аппаратная
 * на Android) закрывает приложение — так и задумано. Обработчик снимается при каждой смене:
 * onClick не заменяет прежний, а добавляет ещё один ([F-7.3.3], та же ловушка, что у MainButton).
 */
export function bindBackButton(visible: boolean, onBack: () => void): () => void {
  return callTelegram((tg) => {
    const b = tg.BackButton
    if (!b) return () => {}
    if (!visible) { b.hide?.(); return () => {} }
    b.onClick?.(onBack)
    b.show?.()
    return () => { b.offClick?.(onBack) }
  }, () => () => {})
}

/** Параметр startapp ссылки t.me/<бот>/<апп>?startapp=… — Telegram кладёт его в initData, а не в query. */
export function startParam(): string | null {
  const fromSdk = getWebApp()?.initDataUnsafe?.start_param
  if (typeof fromSdk === 'string' && fromSdk !== '') return fromSdk
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''))
  const v = hash.get('tgWebAppStartParam')
  return v === null || v === '' ? null : v
}

/** Отклик «получилось» после копирования и сохранения. */
export function hapticSuccess(): void {
  callTelegram((tg) => { tg.HapticFeedback?.notificationOccurred?.('success') }, () => {})
}

/**
 * [F-7.4.23] Копирование — строго в обработчике тапа. Порядок: clipboard API → временное поле
 * и execCommand('copy') → «выделите вручную» (возвращаем false — экран показывает текст выделенным).
 * Чтения буфера в приложении нет нигде: в мини-аппе оно не работает ни на одной платформе.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); return true }
  } catch { /* падаем на запасной путь */ }
  try {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'; ta.style.opacity = '0'; ta.style.top = '0'
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand('copy')
    ta.remove()
    return ok
  } catch {
    return false
  }
}

/** Внешняя ссылка (карточка на сайте Wildberries): внутри Telegram — его браузером, снаружи — новой вкладкой. */
export function openExternal(url: string): void {
  callTelegram((tg) => { if (!tg.openLink) throw new Error('openLink недоступен'); tg.openLink(url) },
    () => { window.open(url, '_blank', 'noopener') })
}

/** Ссылка t.me: «Поделиться» приглашением — родным окном Telegram; снаружи — новой вкладкой. */
export function openTelegram(url: string): void {
  callTelegram((tg) => { if (!tg.openTelegramLink) throw new Error('openTelegramLink недоступен'); tg.openTelegramLink(url) },
    () => { window.open(url, '_blank', 'noopener') })
}

/**
 * Маленькие отметки человека («Как это работает» уже видел) — в CloudStorage Telegram: они идут за
 * человеком на телефон и компьютер. Вне Telegram и на старых клиентах — localStorage этого браузера.
 * Ошибка хранилища не ломает экран: читается как «отметки нет».
 */
type Cloud = {
  getItem: (k: string, cb: (err: unknown, v?: string) => void) => void
  setItem: (k: string, v: string, cb?: (err: unknown, ok?: boolean) => void) => void
}
const cloud = (): Cloud | null => {
  const c = getWebApp()?.CloudStorage as Partial<Cloud> | undefined
  return c !== undefined && typeof c.getItem === 'function' && typeof c.setItem === 'function' ? (c as Cloud) : null
}
export function cloudGet(key: string): Promise<string | null> {
  const c = cloud()
  if (c === null) { try { return Promise.resolve(window.localStorage.getItem(key)) } catch { return Promise.resolve(null) } }
  return new Promise((ok) => {
    const t = setTimeout(() => ok(null), 1500)
    try { c.getItem(key, (err, v) => { clearTimeout(t); ok(err ? null : v === undefined || v === '' ? null : v) }) } catch { clearTimeout(t); ok(null) }
  })
}
export function cloudSet(key: string, value: string): Promise<void> {
  const c = cloud()
  if (c === null) { try { window.localStorage.setItem(key, value) } catch { /* не критично */ } return Promise.resolve() }
  return new Promise((ok) => { try { c.setItem(key, value, () => ok()) } catch { ok() } })
}

// ── Компьютер: Telegram Desktop, macOS и веб-версии (решение пользователя 21.10.2026) ──────────

/** Клиенты на компьютере. Telegram Desktop открывает мини-апп в отдельном узком окне. */
const DESKTOP_PLATFORMS = new Set(['tdesktop', 'macos', 'weba', 'webk', 'web', 'unigram'])

export function isDesktopClient(): boolean {
  return callTelegram((tg) => DESKTOP_PLATFORMS.has(tg.platform ?? ''), () => false)
}

/**
 * «Во весь экран» (Bot API 8.0) — только на компьютере и только если клиент его знает: окно мини-аппа
 * в Telegram Desktop узкое, а развёрнутое на весь экран включает компьютерную раскладку. Клиент без
 * поддержки шлёт fullscreenFailed — кнопка тогда прячется (onFullscreen).
 */
export function canFullscreen(): boolean {
  return callTelegram((tg) => isDesktopClient() && typeof tg.requestFullscreen === 'function' && tg.isVersionAtLeast?.('8.0') === true, () => false)
}

export function isFullscreen(): boolean {
  return callTelegram((tg) => tg.isFullscreen === true, () => false)
}

export function setFullscreen(on: boolean): void {
  callTelegram((tg) => { if (on) tg.requestFullscreen?.(); else tg.exitFullscreen?.() }, () => undefined)
}

/** Подписка на смену режима и на отказ клиента; возвращает отписку. */
export function onFullscreen(cb: (e: 'changed' | 'failed') => void): () => void {
  const changed = (): void => cb('changed'), failed = (): void => cb('failed')
  callTelegram((tg) => { tg.onEvent?.('fullscreenChanged', changed); tg.onEvent?.('fullscreenFailed', failed) }, () => undefined)
  return () => callTelegram((tg) => { tg.offEvent?.('fullscreenChanged', changed); tg.offEvent?.('fullscreenFailed', failed) }, () => undefined)
}
